'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { markSessionStart, recordNavigation, recordUserAction, reportClientError } from '@/app/_lib/clientErrorReporter';

/**
 * Captures uncaught JavaScript errors and unhandled promise rejections, and
 * feeds session context (navigation history, last user action) to the shared
 * reporter so every error report carries it.
 *
 * Mounted once, in the app context provider.
 *
 * Note: this hook cannot see errors caught by a React error boundary — those
 * go to onCaughtError and never reach window.onerror. Boundary errors are
 * reported by app/error.js, app/global-error.js and ErrorBoundary instead.
 */
export function useErrorReporting() {
  const $ = _c(6);
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  let t1;
  let t2;
  if ($[1] !== pathname) {
    t1 = () => {
      recordNavigation(pathname);
      const reloadKey = "server_action_reload_" + pathname;
      if (sessionStorage.getItem(reloadKey)) {
        sessionStorage.removeItem(reloadKey);
      }
    };
    t2 = [pathname];
    $[1] = pathname;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(_temp3, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(_temp6, t4);
}
function _temp6() {
  const handleError = _temp4;
  const handleRejection = _temp5;
  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleRejection);
  return () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleRejection);
  };
}
function _temp5(event_1) {
  const error = event_1.reason;
  const errorMessage = error?.message || String(error) || "";
  if (errorMessage.includes("was not found on the server") || errorMessage.includes("failed-to-find-server-action")) {
    const reloadKey_0 = "server_action_reload_" + window.location.pathname;
    if (sessionStorage.getItem(reloadKey_0)) {
      console.error("Server Action mismatch persists after reload - not retrying");
      sessionStorage.removeItem(reloadKey_0);
    } else {
      console.warn("Server Action mismatch detected - reloading page to get fresh code");
      sessionStorage.setItem(reloadKey_0, Date.now().toString());
      event_1.preventDefault();
      window.location.reload();
      return;
    }
  }
  reportClientError({
    error: error instanceof Error ? error : null,
    type: "unhandledrejection",
    message: errorMessage || "Unhandled promise rejection",
    rawStack: error?.stack || ""
  });
}
function _temp4(event_0) {
  reportClientError({
    error: event_0.error,
    type: "window",
    message: event_0.message,
    rawStack: event_0.error?.stack || `${event_0.filename}:${event_0.lineno}:${event_0.colno}`
  });
}
function _temp3() {
  const handleUserAction = _temp2;
  const onClick = handleUserAction("click");
  const onSubmit = handleUserAction("submit");
  const onKeydown = handleUserAction("keydown");
  const options = {
    capture: true,
    passive: true
  };
  document.addEventListener("click", onClick, options);
  document.addEventListener("submit", onSubmit, options);
  document.addEventListener("keydown", onKeydown, options);
  return () => {
    document.removeEventListener("click", onClick, {
      capture: true
    });
    document.removeEventListener("submit", onSubmit, {
      capture: true
    });
    document.removeEventListener("keydown", onKeydown, {
      capture: true
    });
  };
}
function _temp2(type) {
  return event => {
    const target = event.target;
    const tagName = target?.tagName?.toLowerCase() || "unknown";
    const id = target?.id || "";
    const className = target?.className?.toString?.()?.slice(0, 50) || "";
    recordUserAction({
      type,
      element: `${tagName}${id ? "#" + id : ""}${className ? "." + className.split(" ")[0] : ""}`,
      text: target?.innerText?.slice(0, 30) || ""
    });
  };
}
function _temp() {
  markSessionStart();
}